'use client'

import { DataRenderer, TypeBadge } from '@/components/ui/ExecutionDataDisplay'
import { copilotToolMappings } from '@/utils/copilotToolMappings'
import { ChevronRight } from 'lucide-react'
import React, { useState } from 'react'

interface CopilotToolResultContentProps {
  toolName: string
  content: unknown
  inputs?: unknown
}

// Consistent color for copilot (L1) tools — indigo, matching the Sprites brand
const COPILOT_TOOL_COLOR = '#6366f1'

// Collapsible section — identical markup/classes to NodeExecutionDetailsContent
function CollapsibleSection({
  title,
  subtitle,
  defaultExpanded,
  children,
}: {
  title: string
  subtitle: string
  defaultExpanded: boolean
  children: React.ReactNode
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
      >
        <div className="flex items-center justify-between">
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm font-medium text-gray-600">{subtitle}</p>
          </div>
          <ChevronRight
            size={20}
            className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
              isExpanded ? 'rotate-90' : 'rotate-0'
            }`}
          />
        </div>
      </button>
      <div
        className={`overflow-y-auto transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

function InputsSection({ inputs }: { inputs: unknown }) {
  if (inputs === null || inputs === undefined) return null

  const entries =
    typeof inputs === 'object' && !Array.isArray(inputs)
      ? Object.entries(inputs as Record<string, unknown>)
      : null

  return (
    <CollapsibleSection
      title="Execution Inputs"
      subtitle="Function arguments passed to the tool"
      defaultExpanded={true}
    >
      <div className="space-y-4">
        {entries && entries.length > 0 ? (
          entries.map(([key, value]) => (
            <div key={key} className="rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <h4 className="text-base font-medium text-gray-800">{key}</h4>
                <TypeBadge value={value} />
              </div>
              <DataRenderer data={value} />
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-gray-200 p-4 shadow-sm">
            <DataRenderer data={inputs} />
          </div>
        )}
      </div>
    </CollapsibleSection>
  )
}

function OutputsSection({ content }: { content: unknown }) {
  const isObject = content !== null && typeof content === 'object' && !Array.isArray(content)
  const isArray = Array.isArray(content)

  return (
    <CollapsibleSection
      title="Execution Outputs"
      subtitle="Results returned by the tool execution"
      defaultExpanded={true}
    >
      <div className="space-y-4">
        {isArray ? (
          (content as unknown[]).map((item, index) => (
            <div key={index} className="rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <h4 className="text-base font-medium text-gray-800">Output {index + 1}</h4>
                <TypeBadge value={item} />
              </div>
              <DataRenderer data={item} />
            </div>
          ))
        ) : isObject ? (
          Object.entries(content as Record<string, unknown>).map(([key, value]) => (
            <div key={key} className="rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <h4 className="text-base font-medium text-gray-800">{key}</h4>
                <TypeBadge value={value} />
              </div>
              <DataRenderer data={value} />
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <h4 className="text-base font-medium text-gray-800">Output</h4>
              <TypeBadge value={content} />
            </div>
            <DataRenderer data={content} />
          </div>
        )}
      </div>
    </CollapsibleSection>
  )
}

export function CopilotToolResultContent({
  toolName,
  content,
  inputs,
}: CopilotToolResultContentProps) {
  const toolInfo = copilotToolMappings[toolName as keyof typeof copilotToolMappings]
  const displayName = toolInfo?.displayName ?? toolName
  const icon = toolInfo?.icon

  // Resize the icon element to 24px — copilotToolMappings stores React elements,
  // so we clone with a size prop (react-icons and lucide both accept size)
  const sizedIcon = icon
    ? React.cloneElement(icon as React.ReactElement<{ size?: number }>, { size: 24 })
    : null

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50/30">
      <div className="p-6">
        {/* Header — matches NodeExecutionDetailsContent layout exactly */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 text-white shadow-lg backdrop-blur-sm"
              style={{
                background: `linear-gradient(135deg, ${COPILOT_TOOL_COLOR}dd 0%, ${COPILOT_TOOL_COLOR} 100%)`,
                boxShadow: `0 4px 12px ${COPILOT_TOOL_COLOR}30, 0 0 0 1px ${COPILOT_TOOL_COLOR}20`,
              }}
            >
              {sizedIcon}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{displayName}</h1>
              <p className="text-sm font-medium text-gray-600">Tool Result</p>
            </div>
          </div>
        </div>

        {/* Execution Inputs */}
        {inputs !== undefined && (
          <div className="mb-8">
            <InputsSection inputs={inputs} />
          </div>
        )}

        {/* Execution Outputs */}
        <div className="mb-8">
          <OutputsSection content={content} />
        </div>
      </div>
    </div>
  )
}
